import { useCallback } from 'react'

import SubscriptionEvents from 'Pods/Shared/Constants/subscriptionEvents'
import useTracker from 'Pods/Shared/Hooks/useTracker'

const PAGE_TYPES = {
  checkout: 'subscription_checkout',
  update: 'subscription_update_payment',
  upgrade: 'subscription_upgrade',
}

const SDK_METHOD_MAP = {
  googlePay: 'google_pay',
  applePay: 'apple_pay',
}

const resolvePageType = ({ isUpgradeMode, isUpdateMode }) => {
  if (isUpgradeMode) return PAGE_TYPES.upgrade
  if (isUpdateMode) return PAGE_TYPES.update
  return PAGE_TYPES.checkout
}

const useCheckoutTracking = ({ isUpdateMode = false, isUpgradeMode = false, tier = null } = {}) => {
  const { trackEvent } = useTracker()
  const pageType = resolvePageType({ isUpgradeMode, isUpdateMode })
  const bannerEntry = typeof sessionStorage !== 'undefined'
    && sessionStorage.getItem('subscription_banner_entry') === 'true'

  const viewEventName = isUpdateMode
    ? SubscriptionEvents.UPDATE_PAYMENT_VIEWED
    : SubscriptionEvents.CHECKOUT_VIEWED

  const trackView = useCallback(() => {
    trackEvent(viewEventName, { page_type: pageType, subscription_tier: tier })
  }, [trackEvent, viewEventName, pageType, tier])

  const trackPayClick = useCallback(() => {
    trackEvent(SubscriptionEvents.PAY_CLICK, { page_type: pageType, subscription_tier: tier })
  }, [trackEvent, pageType, tier])

  const trackSuccess = useCallback(() => {
    trackEvent(SubscriptionEvents.PAYMENT_SUCCESS, {
      page_type: pageType,
      subscription_tier: tier,
      ...(bannerEntry && { banner_entry: true }),
    })
  }, [trackEvent, pageType, tier, bannerEntry])

  const trackPaymentMethodSelection = useCallback((method) => {
    const mappedMethod = SDK_METHOD_MAP[method] || method
    trackEvent(SubscriptionEvents.PAYMENT_METHOD_SELECTED, {
      method: mappedMethod,
      page_type: pageType,
      subscription_tier: tier,
    })
  }, [trackEvent, pageType, tier])

  const trackFailure = useCallback((errorMessage) => {
    trackEvent(SubscriptionEvents.PAYMENT_FAILURE, {
      page_type: pageType,
      subscription_tier: tier,
      error_message: errorMessage,
    })
  }, [trackEvent, pageType, tier])

  return {
    trackView,
    trackPayClick,
    trackSuccess,
    trackPaymentMethodSelection,
    trackFailure,
  }
}

export default useCheckoutTracking
